import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { defaultLandingForRole } from "../utils/postLoginRedirect";

/**
 * <AuthCallback />
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Renders at /auth/callback and processes the `#session_id=...` fragment
 * that Emergent Google auth appends after a successful Google consent flow.
 *
 * We synchronously read the hash during the FIRST render, then use `useRef`
 * to gate the effect so React StrictMode's double-invoke never fires the
 * session-exchange twice (per Emergent's playbook).
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const { googleSessionLogin } = useAuth();
  const [status, setStatus] = useState("Signing you in…");
  const processed = useRef(false);

  useEffect(() => {
    // StrictMode safety: exchange the session_id exactly once.
    if (processed.current) return;
    processed.current = true;

    // Parse session_id from URL fragment
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;

    // Parse intended_role from ?intended_role=… (survived Google consent)
    const search = new URLSearchParams(location.search || "");
    const intendedRole = (search.get("intended_role") || "customer").toLowerCase();
    const role = intendedRole === "business" ? "business" : "customer";

    if (!sessionId) {
      setStatus("Missing Google session. Redirecting…");
      setTimeout(() => nav("/login", { replace: true }), 900);
      return;
    }

    (async () => {
      try {
        const r = await googleSessionLogin({ session_id: sessionId, intended_role: role });
        // Route based on what the backend told us the user still needs.
        if (r.needs_business_profile) {
          nav("/business/complete-profile", { replace: true });
          return;
        }
        if (r.needs_dob) {
          nav("/account/complete-profile", { replace: true });
          return;
        }
        // Send the user to the correct role-specific dashboard. Previously
        // `/business/dashboard` was hardcoded here — that route doesn't
        // exist and the wildcard 404 would fire.
        nav(defaultLandingForRole(r.user?.role), { replace: true });
        toast.success("Signed in with Google");
      } catch (e) {
        const msg = e?.response?.data?.detail || "Google sign-in failed. Please try again.";
        toast.error(msg);
        setStatus(msg);
        setTimeout(() => nav("/login", { replace: true }), 1400);
      }
    })();
  }, [nav, location.search, googleSessionLogin]);

  return (
    <div className="App" data-testid="auth-callback-page">
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>BookFreely</div>
          <h1 className="font-display text-3xl mt-2" style={{ color: "var(--gf-text)" }}>{status}</h1>
          <p className="text-sm mt-3" style={{ color: "var(--gf-text-muted)" }}>Hang tight — one moment.</p>
        </div>
      </div>
    </div>
  );
}
